import React, { useEffect, useRef, useState } from 'react'
import { ImageOff } from 'lucide-react'
import { db } from '../../lib/db'

interface AuthedImageProps {
  /** The weeklyCheckIns record this photo belongs to. */
  checkInId: string
  /** Position of this photo within that check-in's photoPaths array. */
  index: number
  alt: string
  className?: string
}

/**
 * Displays a private progress photo by fetching it through the
 * authenticated /api/progress-photo proxy (never a raw InstantDB signed
 * URL) and rendering the bytes as a local, in-memory blob URL.
 *
 * The blob URL never touches the network itself and is revoked as soon as
 * it's no longer needed, so nothing durable or shareable is ever produced
 * for a sensitive photo — unlike InstantDB's own long-lived signed URLs.
 *
 * On any failure (not signed in, not authorized, photo deleted, etc.) this
 * renders a neutral "photo unavailable" placeholder — never a fallback to
 * the original storage URL.
 */
const AuthedImage: React.FC<AuthedImageProps> = ({ checkInId, index, alt, className }) => {
  const { user, isLoading: authLoading } = db.useAuth()
  const token = (user as any)?.refresh_token

  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false

    // Clear any previous image immediately when the identity changes, so we
    // never show a stale photo while the new one loads.
    setBlobUrl(null)
    setFailed(false)

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }

    if (authLoading) return
    if (!token || !checkInId) {
      setFailed(true)
      return
    }

    ;(async () => {
      try {
        const res = await fetch(
          `/api/progress-photo?checkInId=${encodeURIComponent(checkInId)}&index=${index}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cache: 'no-store',
          }
        )
        if (!res.ok) {
          if (!cancelled) setFailed(true)
          return
        }
        const blob = await res.blob()
        if (cancelled) return
        const url = URL.createObjectURL(blob)
        objectUrlRef.current = url
        setBlobUrl(url)
      } catch (err) {
        console.error('AuthedImage: failed to load photo:', err)
        if (!cancelled) setFailed(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [checkInId, index, token, authLoading])

  // Revoke the blob URL on unmount.
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [])

  if (failed || !blobUrl) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-gray-400 ${className || ''}`}
      >
        <ImageOff size={20} />
      </div>
    )
  }

  return <img src={blobUrl} alt={alt} className={className} />
}

export default AuthedImage
